import React, { useRef } from 'react';
import Icon from '../../../components/AppIcon';

const ColorPicker = ({ label, value, onChange }) => (
  <div className="space-y-2">
    <label className="text-sm font-medium text-foreground">{label}</label>
    <div className="flex items-center gap-3">
      <div
        className="w-10 h-10 rounded-md border border-border shadow-sm flex-shrink-0"
        style={{ backgroundColor: value || '#000000' }}
      />
      <div className="flex items-center gap-2 flex-1">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e?.target?.value)}
          className="w-10 h-10 rounded cursor-pointer border border-border p-0.5 bg-background"
        />
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e?.target?.value)}
          placeholder="#000000"
          className="flex-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          maxLength={7}
        />
      </div>
    </div>
  </div>
);

const ImageUploadZone = ({ label, currentUrl, onUpload, uploading, accept = 'image/*' }) => {
  const inputRef = useRef(null);

  const handleDrop = (e) => {
    e?.preventDefault();
    if (uploading) return;
    const file = e?.dataTransfer?.files?.[0];
    if (file) onUpload(file);
  };

  const handleDragOver = (e) => e?.preventDefault();

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">{label}</label>
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors relative ${
          uploading
            ? 'border-primary/50 bg-muted/30 cursor-wait' :'border-border cursor-pointer hover:border-primary/50 hover:bg-muted/30'
        }`}
        onClick={() => { if (!uploading) inputRef?.current?.click(); }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        {/* Loading overlay — shown on top of existing image too */}
        {uploading && (
          <div className="absolute inset-0 rounded-lg bg-background/70 flex flex-col items-center justify-center z-10 gap-2">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary" />
            <p className="text-xs font-medium text-primary">Subiendo...</p>
          </div>
        )}

        {currentUrl ? (
          <div className="space-y-3">
            <img
              src={currentUrl}
              alt={label}
              className="max-h-24 mx-auto object-contain rounded"
            />
            <p className="text-xs text-muted-foreground">Haz clic o arrastra para cambiar</p>
          </div>
        ) : (
          <div className="space-y-2">
            <Icon name="Upload" size={32} className="mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {uploading ? 'Subiendo...' : 'Arrastra una imagen o haz clic para seleccionar'}
            </p>
            <p className="text-xs text-muted-foreground">PNG, JPG, SVG hasta 5MB</p>
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e?.target?.files?.[0];
          if (file) onUpload(file);
          // Reset input so same file can be re-selected
          e.target.value = '';
        }}
      />
    </div>
  );
};

const IdentidadTab = ({ form, onChange, onUploadLogo, onUploadHeader, uploadingLogo, uploadingHeader }) => {
  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-base font-semibold text-foreground mb-4">Imágenes de marca</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ImageUploadZone
            label="Logotipo"
            currentUrl={form?.logo_url}
            onUpload={onUploadLogo}
            uploading={uploadingLogo}
          />
          <ImageUploadZone
            label="Imagen de cabecera"
            currentUrl={form?.header_image_url}
            onUpload={onUploadHeader}
            uploading={uploadingHeader}
          />
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-foreground mb-4">Colores de marca</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <ColorPicker
            label="Color primario"
            value={form?.primary_color}
            onChange={(v) => onChange('primary_color', v)}
          />
          <ColorPicker
            label="Color secundario"
            value={form?.secondary_color}
            onChange={(v) => onChange('secondary_color', v)}
          />
          <ColorPicker
            label="Color de acento"
            value={form?.accent_color}
            onChange={(v) => onChange('accent_color', v)}
          />
        </div>

        <div className="mt-6 p-4 rounded-lg border border-border bg-muted/20">
          <p className="text-xs font-medium text-muted-foreground mb-3">Vista previa de colores</p>
          <div className="flex gap-3">
            <div className="flex-1 rounded-md p-3 text-white text-xs font-medium text-center" style={{ backgroundColor: form?.primary_color || '#E53E3E' }}>
              Primario
            </div>
            <div className="flex-1 rounded-md p-3 text-white text-xs font-medium text-center" style={{ backgroundColor: form?.secondary_color || '#1A1A1A' }}>
              Secundario
            </div>
            <div className="flex-1 rounded-md p-3 text-white text-xs font-medium text-center" style={{ backgroundColor: form?.accent_color || '#718096' }}>
              Acento
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IdentidadTab;
